import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Select, 
  DatePicker, 
  Upload, 
  Button, 
  Row, 
  Col, 
  message, 

  Space,
  Alert,
  Modal,
  Image
} from 'antd';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import { 
  UserOutlined, 
  UploadOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  IdcardOutlined,
  TeamOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import '../AccountOpen.css';

const { Option } = Select;

interface StepContentProps {
  current: number;
  onNext: () => void;
  onPrev: () => void;
}

// 步骤1：会员开立
const MemberRegistrationStep: React.FC<StepContentProps> = ({ current, onNext, onPrev }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewTitle, setPreviewTitle] = useState<string>('');
  // 是否跳过表单校验（临时开关）
  const skipValidation = true;

  // 监听证件类型变化
  const ctrlShrhldIdType = Form.useWatch('ctrl_shrhld_id_tp', form);
  const ctrlShrhldPassportType = Form.useWatch('ctrl_shrhld_passport_tp', form);
  const bnfcIdType = Form.useWatch('bnfc_id_tp', form);
  const bnfcPassportType = Form.useWatch('bnfc_passport_tp', form);
  const lglRprsIdType = Form.useWatch('lgl_rprs_id_tp', form);
  const lglRprsPassportType = Form.useWatch('lgl_rprs_passport_tp', form);
  const contactIdType = Form.useWatch('ctc_id_tp', form);
  const contactPassportType = Form.useWatch('contact_passport_tp', form);

  // 监听企业证件类型变化
  const cmpyIdType = Form.useWatch('cmpy_id_tp', form);
  
  // 条件显示字段的逻辑
  const showTaxAndBusinessFields = cmpyIdType === '2050' || cmpyIdType === '2060'; // 社会团体和民办非企业
  const showCtrlShrhldPassportFields = ctrlShrhldIdType === '1121' || ctrlShrhldIdType === '1122' || ctrlShrhldIdType === '1131'; // 港澳台证件类型
  const showBnfcPassportFields = bnfcIdType === '1121' || bnfcIdType === '1122' || bnfcIdType === '1131'; // 港澳台证件类型
  const showLglRprsPassportFields = lglRprsIdType === '1121' || lglRprsIdType === '1122' || lglRprsIdType === '1131'; // 港澳台证件类型
  const showContactPassportFields = contactIdType === '1121' || contactIdType === '1122' || contactIdType === '1131'; // 港澳台证件类型

  // 监听各上传字段，确保上传/删除后UI立即刷新
  const lgl_rprs_id_front_wRaw = Form.useWatch('lgl_rprs_id_front', form);
  const lgl_rprs_id_front_w = Array.isArray(lgl_rprs_id_front_wRaw) ? lgl_rprs_id_front_wRaw : [];
  const lgl_rprs_id_back_wRaw = Form.useWatch('lgl_rprs_id_back', form);
  const lgl_rprs_id_back_w = Array.isArray(lgl_rprs_id_back_wRaw) ? lgl_rprs_id_back_wRaw : [];
  const ctc_id_front_wRaw = Form.useWatch('ctc_id_front', form);
  const ctc_id_front_w = Array.isArray(ctc_id_front_wRaw) ? ctc_id_front_wRaw : [];
  const ctc_id_back_wRaw = Form.useWatch('ctc_id_back', form);
  const ctc_id_back_w = Array.isArray(ctc_id_back_wRaw) ? ctc_id_back_wRaw : [];
  const bnfc_id_front_wRaw = Form.useWatch('bnfc_id_front', form);
  const bnfc_id_front_w = Array.isArray(bnfc_id_front_wRaw) ? bnfc_id_front_wRaw : [];
  const bnfc_id_back_wRaw = Form.useWatch('bnfc_id_back', form);
  const bnfc_id_back_w = Array.isArray(bnfc_id_back_wRaw) ? bnfc_id_back_wRaw : [];
  const cmpy_id_wRaw = Form.useWatch('cmpy_id', form);
  const cmpy_id_w = Array.isArray(cmpy_id_wRaw) ? cmpy_id_wRaw : [];
  const auth_letter_wRaw = Form.useWatch('auth_letter', form);
  const auth_letter_w = Array.isArray(auth_letter_wRaw) ? auth_letter_wRaw : [];



  // 自动带入的默认值
  const defaultValues = {
    cmpy_nme: '上海数据交易所有限公司',
    cmpy_id_tp: '2011',
    cmpy_id_no: '91310101MA1FP4FQ9A',
    cmpy_id_due_dt: dayjs('2030-12-31'),
    tax_rgsn_cert_no: '',
    bsn_lic_no: '',
    bsn_lic_due_dt: null,
    rgs_addr: '上海市浦东新区张江高科技园区',
    opr_scope: '数据交易服务、数据处理与存储服务、信息技术咨询服务',
    lgl_rprs_nme: '张三',
    lgl_rprs_id_tp: '1101',
    lgl_rprs_id_no: '310101199001011234',
    lgl_rprs_id_vld_dt: dayjs('2030-12-31'),
    lgl_rprs_phone_no: '13900139000',
    ctc_nme: '李四',
    ctc_id_tp: '1101',
    ctc_id_no: '310101199001012345',
    ctc_id_vld_dt: dayjs('2030-12-31'),
    ctc_ctzn: '中国',
    ctc_phone_no: '13900139000',
    // 手动填写字段的默认值
    ctrl_shrhld_nme: '',
    ctrl_shrhld_id_tp: '',
    ctrl_shrhld_id_no: '',
    ctrl_shrhld_id_due_dt: null,
    bnfc_nme: '',
    bnfc_id_tp: '',
    bnfc_id_no: '',
    bnfc_id_due_dt: null,
    bnfc_ctzn: '',
    bnfc_addr: '',
    // 文件上传字段默认值 - 使用图片文件
    lgl_rprs_id_front: [
      {
        uid: '1',
        name: '法定代表人证件正面.jpg',
        status: 'done',
        url: '/idcard.png',
        thumbUrl: '/idcard.png'
      }
    ],
    lgl_rprs_id_back: [
      {
        uid: '2',
        name: '法定代表人证件背面.jpg',
        status: 'done',
        url: '/idcard.png',
        thumbUrl: '/idcard.png'
      }
    ],
    ctc_id_front: [
      {
        uid: '3',
        name: '联系人证件正面.jpg',
        status: 'done',
        url: '/idcard.png',
        thumbUrl: '/idcard.png'
      }
    ],
    ctc_id_back: [
      {
        uid: '4',
        name: '联系人证件背面.jpg',
        status: 'done',
        url: '/idcard.png',
        thumbUrl: '/idcard.png'
      }
    ],
    bnfc_id_front: [],
    bnfc_id_back: [],
    cmpy_id: [
      {
        uid: '99',
        name: '企业证件.jpg',
        status: 'done',
        url: '/idcard.png',
        thumbUrl: '/idcard.png'
      }
    ],
    auth_letter: [],
    // 绑定账户字段默认值
    bind_acct_no_acct_nme: '',
    bind_acct_no: '',
    bind_acct_bank_no: '',
    bind_acct_bank_nme: ''
  };



  // 自动设置通行证类型
  React.useEffect(() => {
    let passportValue = undefined;
    if (ctrlShrhldIdType === '1121') {
      passportValue = '2101';
    } else if (ctrlShrhldIdType === '1122') {
      passportValue = '2102';
    } else if (ctrlShrhldIdType === '1131') {
      passportValue = '2103';
    }
    form.setFieldValue('ctrl_shrhld_passport_tp', passportValue);
  }, [ctrlShrhldIdType, form]);

  React.useEffect(() => {
    let passportValue = undefined;
    if (bnfcIdType === '1121') {
      passportValue = '2101';
    } else if (bnfcIdType === '1122') {
      passportValue = '2102';
    } else if (bnfcIdType === '1131') {
      passportValue = '2103';
    }
    form.setFieldValue('bnfc_passport_tp', passportValue);
  }, [bnfcIdType, form]);

  React.useEffect(() => {
    let passportValue = undefined;
    if (lglRprsIdType === '1121') {
      passportValue = '2101';
    } else if (lglRprsIdType === '1122') {
      passportValue = '2102';
    } else if (lglRprsIdType === '1131') {
      passportValue = '2103';
    }
    form.setFieldValue('lgl_rprs_passport_tp', passportValue);
  }, [lglRprsIdType, form]);

  React.useEffect(() => {
    let passportValue = undefined;
    if (contactIdType === '1121') {
      passportValue = '2101';
    }
    else if (contactIdType === '1122') {
      passportValue = '2102';
    }
    else if (contactIdType === '1131') {
      passportValue = '2103';
    }
    form.setFieldValue('contact_passport_tp', passportValue);
  }, [contactIdType, form]);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success('会员开立申请提交成功！');
      onNext();
    } catch (error) {
      message.error('提交失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 文件上传处理函数
  const handleFileUpload = (info: any, fieldName: string) => {
    const { file } = info;
    // 复制列表并限制为1张
    const nextList = [...info.fileList].slice(-1).map((f: any) => {
      // 为新选择的文件补齐本地预览URL，并立即标记为完成，提升体验
      if (f.originFileObj && !f.url && !f.thumbUrl) {
        const objectUrl = URL.createObjectURL(f.originFileObj);
        return {
          ...f,
          status: 'done',
          url: objectUrl,
          thumbUrl: objectUrl,
        };
      }
      // 确保状态为 done，避免长时间 loading
      if (!f.status || f.status === 'uploading') {
        return { ...f, status: 'done' };
      }
      return f;
    });

    // 同步更新到表单
    form.setFieldsValue({ [fieldName]: nextList });

    // 轻量提示
    if (file.status === 'error') {
      message.error(`${file.name} 上传失败`);
    }
  };

  // 文件预览处理
  const handlePreview = async (file: any) => {
    if (!file.url && !file.preview && file.originFileObj) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
    setPreviewTitle(file.name || '预览');
  };

  // 获取base64编码
  const getBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const commonIdTypes = [
    { value: '1101', label: '第二代居民身份证' },
    { value: '1121', label: '香港居民居住证' },
    { value: '1122', label: '澳门居民居住证' },
    { value: '1131', label: '台湾居民居住证' },
    { value: '1111', label: '外国护照' }
  ];

  // 企业证件类型选项
  const companyIdTypes = [
    { value: '2011', label: '统一社会信用代码' },
    { value: '2040', label: '事业单位法定代表人登记证书' },
    { value: '2050', label: '社会团体法定代表人登记证书' },
    { value: '2060', label: '民办非企业法定代表人登记证书' }
  ];

  // 通行证类型常量
  const passportTypes = [
    { value: '2101', label: '香港居民来往内地通行证' },
    { value: '2102', label: '澳门居民来往内地通行证' },
    { value: '2103', label: '台湾居民来往内地通行证' },
  ];



  // 文件上传配置
  const uploadProps = {
    accept: 'image/*',
    beforeUpload: (file: File) => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        message.error('只能上传图片文件！');
        return false;
      }
      
      const isLt2M = file.size / 1024 / 1024 < 2;
      if (!isLt2M) {
        message.error('图片大小不能超过 2MB!');
        return false;
      }
      
      return true;
    },
    customRequest: ({ file, onSuccess }: any) => {
      // 立即标记成功，去掉延迟，提升响应速度
      onSuccess?.('ok');
    },
    showUploadList: {
      showPreviewIcon: true,
      showRemoveIcon: true,
      showDownloadIcon: false
    },
    // 确保 Upload 内部有可用的预览数据
    previewFile: async (file: any) => {
      return file.thumbUrl || file.url || '';
    },
    onPreview: handlePreview,
    onRemove: (file: any) => {
      // 从对应字段的 fileList 中移除
      const fieldEntry = Object.entries(form.getFieldsValue()).find(([_, v]) => Array.isArray(v) && (v as any[]).some(f => f.uid === file.uid));
      if (fieldEntry) {
        const [fieldName, list] = fieldEntry as [string, any[]];
        const nextList = list.filter(f => f.uid !== file.uid);
        form.setFieldsValue({ [fieldName]: nextList });
      }
      return true;
    }
  };

  return (
    <Card className="enterprise-step-card enterprise-fade-in" style={{ width: '800px', margin: '24px auto 0 auto' }}>
      <div className="enterprise-step-card-header" style={{ background: '#1890ff', color: '#ffffff', padding: '16px 24px', borderRadius: '8px 8px 0 0' }}>
        <UserOutlined style={{ fontSize: '20px', marginRight: '8px' }} />
        <span style={{ fontSize: '18px', fontWeight: 600 }}>第一步：会员开立</span>
      </div>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        className="enterprise-form"
        style={{ padding: '24px', margin: '0 auto' }}
        initialValues={defaultValues}
      >
        {/* 企业基本信息 */}
        <div className="enterprise-form-section" style={{ marginBottom: 32, padding: '24px', background: '#f8faff', borderRadius: '8px' }}>
          <div className="enterprise-form-section-title" style={{ fontSize: '16px', fontWeight: 600, color: '#262626', marginBottom: 16 }}>
            <FileTextOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            企业基本信息
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="cmpy_nme" label="企业名称">
                <Input placeholder="企业名称" disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cmpy_id_tp" label="企业证件类型" rules={[{ required: true, message: '请选择企业证件类型' }]}>
                <Select placeholder="请选择证件种类">
                  {companyIdTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="cmpy_id_no" label="企业证件号码" rules={[{ required: true, message: '请输入企业证件号码' }]}>
                <Input placeholder="企业证件号码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cmpy_id_due_dt" label="企业证件到期日" rules={[{ required: true, message: '请选择企业证件到期日' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="企业证件到期日" />
              </Form.Item>
            </Col>
          </Row>

          {/* 营业执照号码和到期日 - 针对社会团体和民办非企业显示 */}
          {showTaxAndBusinessFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="bsn_lic_no" label="营业执照号码" rules={[{ required: true, message: '请输入营业执照号码' }]}>
                  <Input placeholder="营业执照号码" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="bsn_lic_due_dt" label="营业执照到期日" rules={[{ required: true, message: '请选择营业执照到期日' }]}>
                  <DatePicker style={{ width: '100%' }} placeholder="营业执照到期日" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {/* 税务登记证号码 - 针对社会团体和民办非企业显示 */}
          {showTaxAndBusinessFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="tax_rgsn_cert_no" label="税务登记证号码" rules={[{ required: true, message: '请输入税务登记证号码' }]}>
                  <Input placeholder="税务登记证号码" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Form.Item name="rgs_addr" label="企业注册地址">
            <Input.TextArea rows={2} placeholder="企业注册地址" />
          </Form.Item>
          
          <Form.Item name="opr_scope" label="企业经营范围">
            <Input.TextArea rows={3} placeholder="企业经营范围" />
          </Form.Item>
        </div>

        <div className="enterprise-form-section" style={{ marginBottom: 32, padding: '24px', background: '#f8f9fa', borderRadius: '8px' }}>
          <div className="enterprise-form-section-title" style={{ fontSize: '16px', fontWeight: 600, color: 'var(--enterprise-text-primary)', marginBottom: 16 }}>
            <TeamOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            控股股东信息
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ctrl_shrhld_nme" label="控股股东姓名" rules={[{ required: true, message: '请输入控股股东姓名' }]}>
                <Input placeholder="请输入控股股东姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ctrl_shrhld_id_tp" label="控股股东证件类型" rules={[{ required: true, message: '请选择证件类型' }]}>
                <Select placeholder="请选择证件种类">
                  {commonIdTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ctrl_shrhld_id_no" label="控股股东证件号码" rules={[{ required: true, message: '请输入证件号码' }]}>
                <Input placeholder="请输入控股股东证件号码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ctrl_shrhld_id_due_dt" label="控股股东证件到期日" rules={[{ required: true, message: '请选择证件到期日' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="请选择证件到期日" />
              </Form.Item>
            </Col>
          </Row>

          {/* 控股股东通行证相关字段 */}
          {showCtrlShrhldPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="ctrl_shrhld_passport_tp" label="通行证证件类型">
                  <Select
                    placeholder="请选择通行证类型"
                    value={ctrlShrhldPassportType}
                    disabled
                  >
                    {passportTypes.map(type => (
                      <Option key={type.value} value={type.value}>{type.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="ctrl_shrhld_passport_no" label="通行证号码" rules={[{ required: true, message: '请输入通行证号码' }]}>
                  <Input placeholder="请输入通行证号码" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {showCtrlShrhldPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="ctrl_shrhld_passport_due_dt" label="通行证到期日" rules={[{ required: true, message: '请选择通行证到期日' }]}>
                  <DatePicker style={{ width: '100%' }} placeholder="请选择通行证到期日" />
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>

        {/* 受益人信息 */}
        <div className="enterprise-form-section" style={{ marginBottom: 32, padding: '24px', background: '#f8f9fa', borderRadius: '8px' }}>
          <div className="enterprise-form-section-title" style={{ fontSize: '16px', fontWeight: 600, color: 'var(--enterprise-text-primary)', marginBottom: 16 }}>
            <UserOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            受益人信息
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bnfc_nme" label="受益人姓名" rules={[{ required: true, message: '请输入受益人姓名' }]}>
                <Input placeholder="请输入受益人姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bnfc_id_tp" label="受益人证件类型" rules={[{ required: true, message: '请选择证件类型' }]}>
                <Select placeholder="请选择证件种类">
                  {commonIdTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bnfc_id_no" label="受益人证件号码" rules={[{ required: true, message: '请输入证件号码' }]}>
                <Input placeholder="请输入受益人证件号码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bnfc_id_due_dt" label="受益人证件到期日" rules={[{ required: true, message: '请选择证件到期日' }]}>
                <DatePicker style={{ width: '100%' }} placeholder="请选择证件到期日" />
              </Form.Item>
            </Col>
          </Row>

          {/* 受益人通行证相关字段 */}
          {showBnfcPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="bnfc_passport_tp" label="通行证证件类型">
                  <Select
                    placeholder="请选择通行证类型"
                    value={bnfcPassportType}
                    disabled
                  >
                    {passportTypes.map(type => (
                      <Option key={type.value} value={type.value}>{type.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="bnfc_passport_no" label="通行证号码" rules={[{ required: true, message: '请输入通行证号码' }]}>
                  <Input placeholder="请输入通行证号码" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {showBnfcPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="bnfc_passport_due_dt" label="通行证到期日" rules={[{ required: true, message: '请选择通行证到期日' }]}>
                  <DatePicker style={{ width: '100%' }} placeholder="请选择通行证到期日" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="bnfc_ctzn" label="受益人国籍" rules={[{ required: true, message: '请选择国籍' }]}>
                <Select placeholder="请选择国籍">
                  <Option value="中国">中国</Option>
                  <Option value="中国香港">中国香港</Option>
                  <Option value="中国澳门">中国澳门</Option>
                  <Option value="中国台湾">中国台湾</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item name="bnfc_addr" label="受益人住址" rules={[{ required: true, message: '请输入住址' }]}>
                <Input.TextArea rows={2} placeholder="请输入受益人住址" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* 法定代表人信息 */}
        <div className="enterprise-form-section" style={{ marginBottom: 32, padding: '24px', background: '#f8f9fa', borderRadius: '8px' }}>
          <div className="enterprise-form-section-title" style={{ fontSize: '16px', fontWeight: 600, color: 'var(--enterprise-text-primary)', marginBottom: 16 }}>
            <IdcardOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            法定代表人信息
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="lgl_rprs_nme" label="法定代表人姓名">
                <Input placeholder="法人姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lgl_rprs_id_tp" label="法定代表人证件类型">
                <Select placeholder="请选择证件种类" defaultValue="1101">
                  {commonIdTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="lgl_rprs_id_no" label="法定代表人证件号码">
                <Input placeholder="法人证件号码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lgl_rprs_id_vld_dt" label="法定代表人证件到期日">
                <DatePicker style={{ width: '100%' }} placeholder="法人证件到期日" />
              </Form.Item>
            </Col>
          </Row>

          {/* 法定代表人通行证相关字段 */}
          {showLglRprsPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="lgl_rprs_passport_tp" label="通行证证件类型">
                  <Select
                    placeholder="请选择通行证类型"
                    value={lglRprsPassportType}
                    disabled
                  >
                    {passportTypes.map(type => (
                      <Option key={type.value} value={type.value}>{type.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="lgl_rprs_passport_no" label="通行证号码" rules={[{ required: true, message: '请输入通行证号码' }]}>
                  <Input placeholder="请输入通行证号码" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {showLglRprsPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="lgl_rprs_passport_due_dt" label="通行证到期日" rules={[{ required: true, message: '请选择通行证到期日' }]}>
                  <DatePicker style={{ width: '100%' }} placeholder="请选择通行证到期日" />
                </Form.Item>
              </Col>
            </Row>
          )}

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="lgl_rprs_phone_no" label="法定代表人手机号">
                <Input placeholder="法人手机号" />
              </Form.Item>
            </Col>
          </Row>
        </div>

        {/* 联系人信息 */}
        <div className="enterprise-form-section" style={{ marginBottom: 32, padding: '24px', background: '#f8f9fa', borderRadius: '8px' }}>
          <div className="enterprise-form-section-title" style={{ fontSize: '16px', fontWeight: 600, color: 'var(--enterprise-text-primary)', marginBottom: 16 }}>
            <TeamOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            联系人信息
          </div>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ctc_nme" label="联系人姓名">
                <Input placeholder="联系人姓名" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ctc_id_tp" label="联系人证件类型">
                <Select placeholder="请选择证件种类" defaultValue="1101" disabled>
                  {commonIdTypes.map(type => (
                    <Option key={type.value} value={type.value}>{type.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ctc_id_no" label="联系人证件号码">
                <Input placeholder="联系人证件号码" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ctc_id_vld_dt" label="联系人证件有效期">
                <DatePicker style={{ width: '100%' }} placeholder="联系人证件有效期" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="ctc_ctzn" label="联系人国籍">
                <Select placeholder="请选择国籍" defaultValue="中国" disabled>
                  <Option value="中国">中国</Option>
                  <Option value="中国香港">中国香港</Option>
                  <Option value="中国澳门">中国澳门</Option>
                  <Option value="中国台湾">中国台湾</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ctc_phone_no" label="联系人手机号">
                <Input placeholder="联系人手机号" />
              </Form.Item>
            </Col>
          </Row>

          {/* 联系人通行证相关字段 */}
          {showContactPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contact_passport_tp" label="通行证证件类型">
                  <Select
                    placeholder="请选择通行证类型"
                    value={contactPassportType}
                    disabled
                  >
                    {passportTypes.map(type => (
                      <Option key={type.value} value={type.value}>{type.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="contact_passport_no" label="通行证号码" rules={[{ required: true, message: '请输入通行证号码' }]}>
                  <Input placeholder="请输入通行证号码" />
                </Form.Item>
              </Col>
            </Row>
          )}

          {showContactPassportFields && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="contact_passport_due_dt" label="通行证到期日" rules={[{ required: true, message: '请选择通行证到期日' }]}>
                  <DatePicker style={{ width: '100%' }} placeholder="请选择通行证到期日" />
                </Form.Item>
              </Col>
            </Row>
          )}
        </div>

        {/* 影像信息 */}
        <div className="enterprise-form-section" style={{ marginBottom: 32, padding: '24px', background: '#f8faff', borderRadius: '8px' }}>
          <div className="enterprise-form-section-title" style={{ fontSize: '16px', fontWeight: 600, color: 'var(--enterprise-text-primary)', marginBottom: 16 }}>
            <CameraOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            影像信息
          </div>
          
          <Alert
            message="上传要求"
            description="请确保上传的证件照片清晰完整，支持JPG、PNG格式，单个文件不超过2MB"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="lgl_rprs_id_front" label="法定代表人证件正面">
                <div className="enterprise-upload-section" style={{ minHeight: 180 }}>
                  <Upload
                    {...uploadProps}
                    listType="picture-card"
                    fileList={lgl_rprs_id_front_w}
                    onChange={(info: any) => handleFileUpload(info, 'lgl_rprs_id_front')}
                    onRemove={(file:any) => {
                      const list = form.getFieldValue('lgl_rprs_id_front') || [];
                      const next = list.filter((f:any) => f.uid !== file.uid);
                      form.setFieldsValue({ lgl_rprs_id_front: next });
                      return true;
                    }}
                  >
                    {form.getFieldValue('lgl_rprs_id_front')?.length >= 1 ? null : (
                      <div>
                        <UploadOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                        <div style={{ color: '#1890ff', fontWeight: 600 }}>上传证件正面</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="lgl_rprs_id_back" label="法定代表人证件背面">
                <div className="enterprise-upload-section" style={{ minHeight: 180 }}>
                  <Upload
                    {...uploadProps}
                    listType="picture-card"
                    fileList={lgl_rprs_id_back_w}
                    onChange={(info: any) => handleFileUpload(info, 'lgl_rprs_id_back')}
                    onRemove={(file:any) => {
                      const list = form.getFieldValue('lgl_rprs_id_back') || [];
                      const next = list.filter((f:any) => f.uid !== file.uid);
                      form.setFieldsValue({ lgl_rprs_id_back: next });
                      return true;
                    }}
                  >
                    {form.getFieldValue('lgl_rprs_id_back')?.length >= 1 ? null : (
                      <div>
                        <UploadOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                        <div style={{ color: '#1890ff', fontWeight: 600 }}>上传证件背面</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="ctc_id_front" label="联系人证件正面">
                <div className="enterprise-upload-section" style={{ minHeight: 180 }}>
                  <Upload
                    {...uploadProps}
                    listType="picture-card"
                    fileList={ctc_id_front_w}
                    onChange={(info: any) => handleFileUpload(info, 'ctc_id_front')}
                    onRemove={(file:any) => {
                      const list = form.getFieldValue('ctc_id_front') || [];
                      const next = list.filter((f:any) => f.uid !== file.uid);
                      form.setFieldsValue({ ctc_id_front: next });
                      return true;
                    }}
                  >
                    {form.getFieldValue('ctc_id_front')?.length >= 1 ? null : (
                      <div>
                        <UploadOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                        <div style={{ color: '#1890ff', fontWeight: 600 }}>上传证件正面</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ctc_id_back" label="联系人证件背面">
                <div className="enterprise-upload-section" style={{ minHeight: 180 }}>
                  <Upload
                    {...uploadProps}
                    listType="picture-card"
                    fileList={ctc_id_back_w}
                    onChange={(info: any) => handleFileUpload(info, 'ctc_id_back')}
                    onRemove={(file:any) => {
                      const list = form.getFieldValue('ctc_id_back') || [];
                      const next = list.filter((f:any) => f.uid !== file.uid);
                      form.setFieldsValue({ ctc_id_back: next });
                      return true;
                    }}
                  >
                    {form.getFieldValue('ctc_id_back')?.length >= 1 ? null : (
                      <div>
                        <UploadOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                        <div style={{ color: '#1890ff', fontWeight: 600 }}>上传证件背面</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="bnfc_id_front" label="受益人证件正面">
                <div className="enterprise-upload-section" style={{ minHeight: 180 }}>
                  <Upload
                    {...uploadProps}
                    listType="picture-card"
                    fileList={bnfc_id_front_w}
                    onChange={(info: any) => handleFileUpload(info, 'bnfc_id_front')}
                    onRemove={(file:any) => {
                      const list = form.getFieldValue('bnfc_id_front') || [];
                      const next = list.filter((f:any) => f.uid !== file.uid);
                      form.setFieldsValue({ bnfc_id_front: next });
                      return true;
                    }}
                  >
                    {form.getFieldValue('bnfc_id_front')?.length >= 1 ? null : (
                      <div>
                        <UploadOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                        <div style={{ color: '#1890ff', fontWeight: 600 }}>上传证件正面</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="bnfc_id_back" label="受益人证件背面">
                <div className="enterprise-upload-section" style={{ minHeight: 180 }}>
                  <Upload
                    {...uploadProps}
                    listType="picture-card"
                    fileList={bnfc_id_back_w}
                    onChange={(info: any) => handleFileUpload(info, 'bnfc_id_back')}
                    onRemove={(file:any) => {
                      const list = form.getFieldValue('bnfc_id_back') || [];
                      const next = list.filter((f:any) => f.uid !== file.uid);
                      form.setFieldsValue({ bnfc_id_back: next });
                      return true;
                    }}
                  >
                    {form.getFieldValue('bnfc_id_back')?.length >= 1 ? null : (
                      <div>
                        <UploadOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                        <div style={{ color: '#1890ff', fontWeight: 600 }}>上传证件背面</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item name="cmpy_id" label="企业证件" style={{ marginBottom: 24 }}>
                <div className="enterprise-upload-section" style={{ minHeight: 180 }}>
                  <Upload
                    {...uploadProps}
                    listType="picture-card"
                    fileList={cmpy_id_w}
                    onChange={(info: any) => handleFileUpload(info, 'cmpy_id')}
                    onRemove={(file:any) => {
                      const list = form.getFieldValue('cmpy_id') || [];
                      const next = list.filter((f:any) => f.uid !== file.uid);
                      form.setFieldsValue({ cmpy_id: next });
                      return true;
                    }}
                  >
                    {form.getFieldValue('cmpy_id')?.length >= 1 ? null : (
                      <div>
                        <UploadOutlined style={{ fontSize: 24, color: '#1890ff', marginBottom: 8 }} />
                        <div style={{ color: '#1890ff', fontWeight: 600 }}>上传企业证件</div>
                      </div>
                    )}
                  </Upload>
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="auth_letter" label="授权书">
            <div className="enterprise-upload-section">
              <Alert
                message="上传要求"
                description="请下载模板填写完整并加盖公章后上传。支持 PDF、Word 或图片文件，单个文件建议不超过 5MB。"
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
              />
              <Space>
                <Button 
                  icon={<FileTextOutlined />}
                  size="large"
                  style={{
                    background: '#1890ff',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontWeight: 600
                  }}
                  onClick={() => {
                    // 模拟下载授权书模板
                    message.info('授权书模板下载功能正在开发中');
                  }}
                >
                  下载授权书模板
                </Button>
                <Upload
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  listType="text"
                  fileList={auth_letter_w}
                  onChange={(info: any) => handleFileUpload(info, 'auth_letter')}
                  onRemove={(file:any) => {
                    const list = auth_letter_w || [];
                    const next = list.filter((f:any) => f.uid !== file.uid);
                    form.setFieldsValue({ auth_letter: next });
                    return true;
                  }}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                    showDownloadIcon: true
                  }}
                  itemRender={(
                    originNode: React.ReactNode, 
                    file: UploadFile<any>, 
                    fileList: UploadFile<any>[], 
                    actions: { download?: () => void; remove?: () => void }
                  ) => (
                    <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%'}}>
                      <span title={file.name} style={{flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{file.name}</span>
                      <span style={{display:'inline-flex', gap:12}}>
                        <Button 
                          type="link"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => { 
                            e.preventDefault(); 
                            // 强制下载而非打开
                            const name = file.name || '授权书';
                            let href = file.url || file.thumbUrl || '';
                            let needRevoke = false;
                            if (!href && (file as any).originFileObj) {
                              href = URL.createObjectURL((file as any).originFileObj);
                              needRevoke = true;
                            }
                            if (!href) {
                              message.warning('暂无可下载的文件');
                              return;
                            }
                            const a = document.createElement('a');
                            a.href = href;
                            a.download = name;
                            a.rel = 'noopener';
                            a.target = '_self';
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            if (needRevoke) {
                              URL.revokeObjectURL(href);
                            }
                          }} 
                          style={{color:'#1890ff', padding: 0}}
                        >
                          下载
                        </Button>
                        <Button 
                          type="link"
                          danger
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => { 
                            e.preventDefault(); 
                            actions.remove?.(); 
                          }} 
                          style={{padding: 0}}
                        >
                          删除
                        </Button>
                      </span>
                    </div>
                  )}
                  beforeUpload={() => true}
                  customRequest={({ onSuccess }: any) => onSuccess?.('ok')}
                  onPreview={(file: any) => {
                    const isImg = (file.type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test((file.url || file.thumbUrl || ''));
                    if (isImg) {
                      handlePreview(file);
                    } else {
                      message.info('该文件类型不支持预览，请下载查看');
                    }
                  }}
                >
                  {Array.isArray(auth_letter_w) && auth_letter_w.length >= 1 ? null : (
                    <Button 
                      icon={<UploadOutlined />}
                      size="large"
                      style={{
                        background: '#1890ff',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#ffffff',
                        fontWeight: 600
                      }}
                    >
                      上传盖章后的授权书
                    </Button>
                  )}
                </Upload>
              </Space>
              <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '8px' }}>
                授权书模板下载后请填写完整并加盖公章，上传后系统将进行审核确认
              </div>
            </div>
          </Form.Item>
        </div>

        <div className="enterprise-action-buttons" style={{ textAlign: 'center', marginTop: 32 }}>
          <Button 
            type="primary"
            loading={loading}
            onClick={() => {
              if (skipValidation) {
                handleSubmit(form.getFieldsValue());
              } else {
                form.submit();
              }
            }}
            style={{
              height: '48px',
              fontSize: '16px',
              fontWeight: 600,
              padding: '0 40px',
              background: '#1890ff',
              border: 'none',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(114, 46, 209, 0.3)'
            }}
            icon={<CheckCircleOutlined />}
          >
            {loading ? '提交中...' : '提交申请'}
          </Button>
        </div>
        {/* 统一图片预览 Modal */}
        <Modal
          open={previewOpen}
          title={previewTitle}
          footer={null}
          onCancel={() => setPreviewOpen(false)}
          centered
          width={720}
        >
          {previewImage ? (
            <Image
              src={previewImage}
              alt={previewTitle}
              style={{ width: '100%' }}
            />
          ) : null}
        </Modal>
      </Form>
    </Card>
  );
};

export default MemberRegistrationStep;